import type { MaybeRefOrGetter } from 'vue';

export type UseListboxNavigationOptions<T> = {
    /** The options in display order. Compared with `===`: pass stable values (ids, the same objects) */
    items: MaybeRefOrGetter<readonly T[]>;
    /** Disabled options are skipped */
    isDisabled?: (item: T) => boolean;
    /** Arrow keys wrap from the last option to the first. Default `false` */
    loop?: MaybeRefOrGetter<boolean>;
    /** Home and End jump to the first and last option. Default `true`; a combobox passes `false` so they move the caret */
    homeEnd?: MaybeRefOrGetter<boolean>;
    /** Enter on the active option */
    onSelect: (item: T, event: KeyboardEvent) => void;
};

/**
 * Keyboard navigation for a listbox that keeps focus elsewhere (a combobox input, a select
 * trigger): the active-descendant pattern. The composable tracks which option is active; the
 * component puts that option's id in `aria-activedescendant` on the focused element, marks
 * the option, and scrolls it into view.
 *
 * Keys handled by `onKeydown`: ArrowDown, ArrowUp, Home, End, Enter. It leaves events with
 * Alt, Ctrl or Meta alone, and leaves Escape to `useDismiss`.
 *
 * @example
 * const nav = useListboxNavigation({ items: () => filtered.value.map((o) => o.id), onSelect: select });
 * // <input :aria-activedescendant="nav.active.value && optionId(nav.active.value)" @keydown="nav.onKeydown" />
 */
export function useListboxNavigation<T>(options: UseListboxNavigationOptions<T>) {
    const active = shallowRef<T>();

    const items = computed(() => toValue(options.items));
    const isEnabled = (item: T) => !options.isDisabled?.(item);
    const activeIndex = computed(() => (active.value === undefined ? -1 : items.value.indexOf(active.value)));

    /* An active option that leaves the list (filtered out, removed) stops being active. */
    watch(items, () => {
        if (activeIndex.value === -1 || !isEnabled(active.value as T)) active.value = undefined;
    });

    function highlight(item: T | undefined) {
        active.value = item !== undefined && items.value.includes(item) && isEnabled(item) ? item : undefined;
    }

    /* The first enabled option from `start` in `step` direction, wrapping when `loop` is set. */
    function seek(start: number, step: 1 | -1): T | undefined {
        const list = items.value;
        const loop = toValue(options.loop) ?? false;
        for (let offset = 0; offset < list.length; offset++) {
            let index = start + offset * step;
            if (loop) index = (index + list.length) % list.length;
            const item = list[index];
            if (item === undefined) return undefined;
            if (isEnabled(item)) return item;
        }
        return undefined;
    }

    const first = () => (active.value = seek(0, 1));
    const last = () => (active.value = seek(items.value.length - 1, -1));

    function next() {
        if (activeIndex.value === -1) return first();
        active.value = seek(activeIndex.value + 1, 1) ?? active.value;
    }

    function previous() {
        if (activeIndex.value === -1) return last();
        active.value = seek(activeIndex.value - 1, -1) ?? active.value;
    }

    function onKeydown(event: KeyboardEvent) {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        const homeEnd = toValue(options.homeEnd) ?? true;

        switch (event.key) {
            case 'ArrowDown':
                next();
                break;
            case 'ArrowUp':
                previous();
                break;
            case 'Home':
                if (!homeEnd) return;
                first();
                break;
            case 'End':
                if (!homeEnd) return;
                last();
                break;
            case 'Enter':
                if (active.value === undefined) return;
                options.onSelect(active.value, event);
                break;
            default:
                return;
        }
        event.preventDefault();
    }

    return { active: computed(() => active.value), activeIndex, highlight, first, last, next, previous, onKeydown };
}
